import React, { useState, useContext } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Card, ProgressBar, Badge, Table, Button, Form, Modal } from "react-bootstrap";
import { FloatCardComponent } from "../../components/cards";
import PageLayout from "../../layouts/PageLayout";

export default function FondosPresupuestoPage() {
    const { t } = useContext(TranslatorContext);
    
    // Estado para el año seleccionado
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    
    // Estado para modales
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState(''); // 'create', 'edit', 'delete'
    const [selectedFondo, setSelectedFondo] = useState(null);
    
    // Estado para formulario
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        presupuesto: '',
        color: '#007bff',
        activo: true
    });

    // Datos de fondos por año
    const [fondosPorAnio, setFondosPorAnio] = useState({
        2024: [
            {
                id: 1,
                nombre: "Fondo Emergencia",
                descripcion: "Fondo para situaciones de emergencia institucional",
                presupuesto: 2400000,
                gastado: 1200000,
                color: "#dc3545",
                icon: "emergency",
                categoria: "gastos_oficina",
                items: 8,
                activo: true,
                fechaCreacion: "2024-01-01"
            },
            {
                id: 2,
                nombre: "Fondo Inversión",
                descripcion: "Fondo destinado a inversiones estratégicas",
                presupuesto: 3000000,
                gastado: 1500000,
                color: "#0dcaf0",
                icon: "trending_up",
                categoria: "edmundo_neira",
                items: 10,
                activo: true,
                fechaCreacion: "2024-01-01"
            },
            {
                id: 3,
                nombre: "Fondo Capitalización",
                descripcion: "Fondo para capitalización de la empresa",
                presupuesto: 1200000,
                gastado: 800000,
                color: "#198754",
                icon: "savings",
                categoria: "operativa_terreno",
                items: 4,
                activo: true,
                fechaCreacion: "2024-01-01"
            },
            {
                id: 4,
                nombre: "Fondo Capacidad Laboral",
                descripcion: "Fondo para desarrollo de capacidades del personal",
                presupuesto: 3000000,
                gastado: 2200000,
                color: "#ffc107",
                icon: "group",
                categoria: "egresos_operativos",
                items: 10,
                activo: true,
                fechaCreacion: "2024-01-01"
            }
        ],
        2025: [
            {
                id: 5,
                nombre: "Fondo Emergencia",
                descripcion: "Fondo para situaciones de emergencia institucional",
                presupuesto: 3000000,
                gastado: 800000,
                color: "#dc3545",
                icon: "emergency",
                categoria: "gastos_oficina",
                items: 5,
                activo: true,
                fechaCreacion: "2025-01-01"
            },
            {
                id: 6,
                nombre: "Fondo Tecnología",
                descripcion: "Fondo para mejoras tecnológicas",
                presupuesto: 2500000,
                gastado: 500000,
                color: "#6f42c1",
                icon: "computer",
                categoria: "tecnologia",
                items: 3,
                activo: true,
                fechaCreacion: "2025-01-01"
            }
        ]
    });

    // Obtener fondos del año seleccionado
    const fondos = fondosPorAnio[selectedYear] || [];

    // Generar años disponibles
    const generarAniosDisponibles = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear; i >= currentYear - 4; i--) {
            years.push(i);
        }
        return years;
    };

    // Resumen general
    const totalPresupuesto = fondos.reduce((sum, fondo) => sum + fondo.presupuesto, 0);
    const totalGastado = fondos.reduce((sum, fondo) => sum + fondo.gastado, 0);
    const totalDisponible = totalPresupuesto - totalGastado;
    const porcentajeEjecucion = totalPresupuesto > 0 ? (totalGastado / totalPresupuesto) * 100 : 0;

    const floats = [
        { 
            "title": "Presupuesto Total", 
            "digit": totalPresupuesto, 
            "icon": "account_balance_wallet", 
            "variant": "lg blue" 
        }, 
        { 
            "title": "Total Ejecutado", 
            "digit": totalGastado, 
            "icon": "trending_down", 
            "variant": "lg red" 
        },
        { 
            "title": "Saldo Disponible", 
            "digit": totalDisponible, 
            "icon": "savings", 
            "variant": "lg green" 
        },
        { 
            "title": "Fondos Activos", 
            "digit": fondos.filter(f => f.activo).length, 
            "icon": "folder", 
            "variant": "lg purple" 
        }
    ];

    const getStatusBadge = (porcentaje) => {
        if (porcentaje < 50) return { variant: "success", text: "Bajo" };
        if (porcentaje < 80) return { variant: "warning", text: "Medio" };
        return { variant: "danger", text: "Alto" };
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    // Funciones para manejar el modal
    const abrirModal = (tipo, fondo = null) => {
        setModalType(tipo);
        setSelectedFondo(fondo);
        if (fondo) {
            setFormData({
                nombre: fondo.nombre,
                descripcion: fondo.descripcion,
                presupuesto: fondo.presupuesto.toString(),
                color: fondo.color,
                activo: fondo.activo
            });
        } else {
            setFormData({
                nombre: '',
                descripcion: '',
                presupuesto: '',
                color: '#007bff',
                activo: true
            });
        }
        setShowModal(true);
    };

    const cerrarModal = () => {
        setShowModal(false);
        setModalType('');
        setSelectedFondo(null);
        setFormData({
            nombre: '',
            descripcion: '',
            presupuesto: '',
            color: '#007bff',
            activo: true
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const guardarFondo = () => {
        if (modalType === 'create') {
            const nuevoFondo = {
                id: Date.now(),
                nombre: formData.nombre,
                descripcion: formData.descripcion,
                presupuesto: parseFloat(formData.presupuesto),
                gastado: 0,
                color: formData.color,
                icon: "folder",
                categoria: "general",
                items: 0,
                activo: formData.activo,
                fechaCreacion: new Date().toISOString().split('T')[0]
            };

            setFondosPorAnio(prev => ({
                ...prev,
                [selectedYear]: [...(prev[selectedYear] || []), nuevoFondo]
            }));
        } else if (modalType === 'edit') {
            setFondosPorAnio(prev => ({
                ...prev,
                [selectedYear]: prev[selectedYear].map(fondo => 
                    fondo.id === selectedFondo.id 
                        ? {
                            ...fondo,
                            nombre: formData.nombre,
                            descripcion: formData.descripcion,
                            presupuesto: parseFloat(formData.presupuesto),
                            color: formData.color,
                            activo: formData.activo
                        }
                        : fondo
                )
            }));
        }
        cerrarModal();
    };

    const toggleEstadoFondo = (fondoId) => {
        setFondosPorAnio(prev => ({
            ...prev,
            [selectedYear]: prev[selectedYear].map(fondo => 
                fondo.id === fondoId 
                    ? { ...fondo, activo: !fondo.activo }
                    : fondo
            )
        }));
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">Mantenedor de Fondos</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                                <li className="mc-breadcrumb-item"><Link to='/gastos' className="mc-breadcrumb-link">Gastos</Link></li>
                                <li className="mc-breadcrumb-item">Fondos</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Selector de Año y Controles */}
                <Col xl={12}>
                    <Card className="mc-card mb-4">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Año de Gestión</Form.Label>
                                        <Form.Select 
                                            value={selectedYear} 
                                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                            className="form-select-lg"
                                        >
                                            {generarAniosDisponibles().map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <div className="text-center">
                                        <h5 className="text-muted mb-1">Gestión Presupuestaria {selectedYear}</h5>
                                        <p className="mb-0">Total de {fondos.length} fondos configurados</p>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-grid gap-2">
                                        <Button 
                                            variant="primary" 
                                            onClick={() => abrirModal('create')}
                                        >
                                            <i className="material-icons me-2">add</i>
                                            Crear Fondo
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Cards de resumen */}
                {floats.map((float, index) => (
                    <Col key={index} sm={6} lg={3}>
                        <FloatCardComponent 
                            variant={float.variant}
                            digit={typeof float.digit === 'number' ? formatCurrency(float.digit) : float.digit}
                            title={float.title}
                            icon={float.icon}
                        />
                    </Col>
                ))}

                {/* Detalle de fondos */}
                <Col xl={12}>
                    <Card className="mc-card">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h4>Fondos Configurados - {selectedYear}</h4>
                            <div>
                                <Button variant="outline-success" size="sm" className="me-2">
                                    <i className="material-icons me-1">download</i>
                                    Exportar
                                </Button>
                                <Button variant="outline-primary" size="sm">
                                    <i className="material-icons me-1">settings</i>
                                    Configurar
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                {fondos.length === 0 ? (
                                    <Col xl={12}>
                                        <div className="text-center py-5">
                                            <i className="material-icons text-muted" style={{fontSize: '48px'}}>folder_open</i>
                                            <h5 className="text-muted mt-3">No hay fondos configurados para {selectedYear}</h5>
                                            <p className="text-muted">Crea tu primer fondo para comenzar a gestionar el presupuesto</p>
                                            <Button 
                                                variant="primary" 
                                                onClick={() => abrirModal('create')}
                                            >
                                                <i className="material-icons me-2">add</i>
                                                Crear Primer Fondo
                                            </Button>
                                        </div>
                                    </Col>
                                ) : (
                                    fondos.map((fondo, index) => {
                                        const porcentajeUsado = fondo.presupuesto > 0 ? (fondo.gastado / fondo.presupuesto) * 100 : 0;
                                        const saldoDisponible = fondo.presupuesto - fondo.gastado;
                                        const status = getStatusBadge(porcentajeUsado);

                                        return (
                                            <Col key={index} md={6} lg={6} className="mb-4">
                                                <Card style={{ 
                                                    borderLeft: `4px solid ${fondo.color}`, 
                                                    opacity: fondo.activo ? 1 : 0.6 
                                                }}>
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <div>
                                                                <h5 className="mb-0">{fondo.nombre}</h5>
                                                                <small className="text-muted">{fondo.descripcion}</small>
                                                            </div>
                                                            <div className="d-flex align-items-center">
                                                                <Badge bg={status.variant} className="me-2">{status.text}</Badge>
                                                                <Badge bg={fondo.activo ? "success" : "secondary"}>
                                                                    {fondo.activo ? "Activo" : "Inactivo"}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="mb-3">
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span>Presupuesto:</span>
                                                                <strong>{formatCurrency(fondo.presupuesto)}</strong>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span>Ejecutado:</span>
                                                                <span style={{ color: fondo.color }}>
                                                                    {formatCurrency(fondo.gastado)}
                                                                </span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span>Disponible:</span>
                                                                <strong className="text-success">
                                                                    {formatCurrency(saldoDisponible)}
                                                                </strong>
                                                            </div>
                                                        </div>

                                                        <ProgressBar 
                                                            now={porcentajeUsado} 
                                                            variant={porcentajeUsado > 80 ? "danger" : porcentajeUsado > 60 ? "warning" : "success"}
                                                            style={{ height: '10px' }}
                                                        />
                                                        <div className="d-flex justify-content-between mt-2">
                                                            <small className="text-muted">{porcentajeUsado.toFixed(1)}% usado</small>
                                                            <small className="text-muted">{fondo.items} gastos</small>
                                                        </div>

                                                        {/* Botones de acción */}
                                                        <div className="d-flex justify-content-between mt-3">
                                                            <div>
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm" 
                                                                    className="me-2"
                                                                    onClick={() => abrirModal('edit', fondo)}
                                                                >
                                                                    <i className="material-icons">edit</i>
                                                                </Button>
                                                                <Button 
                                                                    variant={fondo.activo ? "outline-warning" : "outline-success"} 
                                                                    size="sm"
                                                                    onClick={() => toggleEstadoFondo(fondo.id)}
                                                                >
                                                                    <i className="material-icons">
                                                                        {fondo.activo ? "visibility_off" : "visibility"}
                                                                    </i>
                                                                </Button>
                                                            </div>
                                                            <Button variant="outline-info" size="sm">
                                                                <i className="material-icons me-1">analytics</i>
                                                                Ver Detalle
                                                            </Button>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        );
                                    })
                                )}
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Tabla resumen de fondos */}
                {fondos.length > 0 && (
                    <Col xl={12}>
                        <Card className="mc-card">
                            <Card.Header>
                                <h4>Resumen de Fondos - {selectedYear}</h4>
                            </Card.Header>
                            <Card.Body>
                                <Table responsive striped>
                                    <thead>
                                        <tr>
                                            <th>Tipo</th>
                                            <th>Fondo Asignado</th>
                                            <th>Presupuesto</th>
                                            <th>Ejecutado</th>
                                            <th>Disponible</th>
                                            <th>% Ejecución</th>
                                            <th>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fondos.filter(f => f.activo).map((fondo, index) => {
                                            const porcentajeUsado = fondo.presupuesto > 0 ? (fondo.gastado / fondo.presupuesto) * 100 : 0;
                                            const saldoDisponible = fondo.presupuesto - fondo.gastado;
                                            const status = getStatusBadge(porcentajeUsado);

                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div 
                                                                className="me-2" 
                                                                style={{
                                                                    width: '12px',
                                                                    height: '12px',
                                                                    backgroundColor: fondo.color,
                                                                    borderRadius: '2px'
                                                                }}
                                                            ></div>
                                                            FONDO GENERAL
                                                        </div>
                                                    </td>
                                                    <td>{fondo.nombre}</td>
                                                    <td>{formatCurrency(fondo.presupuesto)}</td>
                                                    <td style={{ color: fondo.color }}>
                                                        {formatCurrency(fondo.gastado)}
                                                    </td>
                                                    <td className="text-success">
                                                        {formatCurrency(saldoDisponible)}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div style={{ width: '60px', marginRight: '8px' }}>
                                                                <ProgressBar 
                                                                    now={porcentajeUsado} 
                                                                    style={{ height: '6px' }}
                                                                    variant={porcentajeUsado > 80 ? "danger" : porcentajeUsado > 60 ? "warning" : "success"}
                                                                />
                                                            </div>
                                                            <small>{porcentajeUsado.toFixed(1)}%</small>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge bg={status.variant}>{status.text}</Badge>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>

            {/* Modal para crear/editar fondos */}
            <Modal show={showModal} onHide={cerrarModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalType === 'create' ? 'Crear Nuevo Fondo' : 'Editar Fondo'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nombre del Fondo *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="nombre"
                                        value={formData.nombre}
                                        onChange={handleInputChange}
                                        placeholder="Ej: Fondo Emergencia"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Color</Form.Label>
                                    <Form.Control
                                        type="color"
                                        name="color"
                                        value={formData.color}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Descripción</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="descripcion"
                                        value={formData.descripcion}
                                        onChange={handleInputChange}
                                        placeholder="Descripción del propósito del fondo"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Presupuesto Anual *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="presupuesto"
                                        value={formData.presupuesto}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        min="0"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        name="activo"
                                        checked={formData.activo}
                                        onChange={handleInputChange}
                                        label="Fondo activo"
                                        className="mt-4"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cerrarModal}>
                        Cancelar
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={guardarFondo}
                        disabled={!formData.nombre || !formData.presupuesto}
                    >
                        {modalType === 'create' ? 'Crear Fondo' : 'Guardar Cambios'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}
